import React from 'react'
import { Box, Link, Paper, Typography } from '@mui/material'
import { Table } from 'react-bootstrap'
import MuiFilterSelect, { DEFAULT_FILTER_VALUE } from '../../components/Filters/MuiFilterSelect'
import SearchInput from '../../components/SearchInput'
import SettlementDetailContextHeader from '../../components/SettlementDetailContextHeader'
import { STATUS_CANCELLED } from '../../constants/chargeConstants'
import {
  SETTLEMENT_DOCUMENTS_EMPTY_STATE,
  SETTLEMENT_DOCUMENTS_FILTER_LABEL,
  SETTLEMENT_DOCUMENTS_MISSING_VALUE,
  SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CASH,
  SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CREDIT,
  SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_FILTER_TITLE,
  SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_OPTIONS,
  SETTLEMENT_DOCUMENTS_RESET_LABEL,
  SETTLEMENT_DOCUMENTS_ROUTE_HEADER,
  SETTLEMENT_DOCUMENTS_SEARCH_PLACEHOLDER,
  SETTLEMENT_DOCUMENTS_TABLE_HEADERS,
  SETTLEMENT_DOCUMENTS_TITLE
} from '../../constants/settlementDetailsConstants'
import GoogleIcon from '../../icon/GoogleIcon'
import { getTimestampFromDateTime } from '../../helpers/formatDate'
import { formatMoney, parseDocumentType } from '../../helpers/utils'
import { isDocumentCredit, sortNewestFirst } from '../helpers/detailsHelpers'

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export default function SettlementDocumentsTab({ settlement = null, actions = null, canWrite = false }) {
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState(DEFAULT_FILTER_VALUE)
  const [search, setSearch] = React.useState('')

  const details = React.useMemo(() => settlement?.details || [], [settlement])
  const hasDocumentRoute = React.useMemo(
    () => details.some((detail) => Boolean(String(detail?.child_document_route || '').trim())),
    [details]
  )
  const shouldShowRouteColumn = canWrite || hasDocumentRoute
  const tableHeaders = React.useMemo(() => {
    if (!shouldShowRouteColumn) return SETTLEMENT_DOCUMENTS_TABLE_HEADERS

    return [
      ...SETTLEMENT_DOCUMENTS_TABLE_HEADERS.slice(0, 5),
      SETTLEMENT_DOCUMENTS_ROUTE_HEADER,
      ...SETTLEMENT_DOCUMENTS_TABLE_HEADERS.slice(5)
    ]
  }, [shouldShowRouteColumn])

  const rows = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    const paymentTypeValues =
      paymentTypeFilter?.mode === 'custom'
        ? paymentTypeFilter.values.map((value) => normalizeText(value))
        : []

    return sortNewestFirst(details)
      .filter((charge) => charge.charge_status !== STATUS_CANCELLED)
      .filter((detail) => {
        const paymentType = isDocumentCredit(detail)
          ? SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CREDIT
          : SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CASH

        if (paymentTypeValues.length > 0 && !paymentTypeValues.includes(normalizeText(paymentType))) {
          return false
        }

        if (!normalizedSearch) return true

        return (
          String(detail.child_document_reference || '')
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(detail.charge_uid || '')
            .toLowerCase()
            .includes(normalizedSearch)
        )
      })
  }, [details, paymentTypeFilter, search])

  const shouldShowReset = React.useMemo(() => {
    return paymentTypeFilter?.mode === 'custom' || Boolean(search.trim())
  }, [paymentTypeFilter, search])

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = () => {
    setSearch('')
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
  }

  const handleResetFilters = () => {
    setPaymentTypeFilter(DEFAULT_FILTER_VALUE)
    setSearch('')
  }

  return (
    <Box>
      <SettlementDetailContextHeader
        title={SETTLEMENT_DOCUMENTS_TITLE}
        settlement={settlement}
        actions={actions}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {SETTLEMENT_DOCUMENTS_FILTER_LABEL}
      </Typography>

      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={3}>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" flex={1}>
          <GoogleIcon className="filter-icon-poc oc-icon-primary" name="page_info" size={22} />

          <MuiFilterSelect
            title={SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_FILTER_TITLE}
            options={SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_OPTIONS}
            value={paymentTypeFilter}
            onChange={setPaymentTypeFilter}
            width={220}
            dropdownWidth={280}
            matchTriggerWidth
          />
        </Box>

        <Box flexShrink={0} mt={0.6}>
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            onEnter={handleSearchEnter}
            onClear={handleClearSearch}
            placeholder={SETTLEMENT_DOCUMENTS_SEARCH_PLACEHOLDER}
          />
        </Box>
      </Box>

      {shouldShowReset && (
        <Typography variant="body2" className="oc-reset-filter" onClick={handleResetFilters}>
          {SETTLEMENT_DOCUMENTS_RESET_LABEL}
        </Typography>
      )}

      <Paper elevation={0} sx={{ overflow: 'hidden' }}>
        <Table hover responsive>
          <thead className="table-header-gray-mui">
            <tr>
              {tableHeaders.map((header) => (
                <th
                  key={header}
                  className={
                    header === 'SALDO' || header === 'PAGADO'
                      ? 'td-number-align-right'
                      : undefined
                  }
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((model, index) => (
                <tr key={model.uuid || `${model.child_document_reference}-${index}`}>
                  <td className="td-text-limited">
                    {getTimestampFromDateTime(model?.date_of_charge)}
                  </td>
                  <td className="td-text-limited">{model?.client_uid}</td>
                  <td className="td-text-limited">{model?.client_name}</td>
                  <td className="td-text-limited">
                    {model?.child_document_reference || SETTLEMENT_DOCUMENTS_MISSING_VALUE}
                  </td>
                  <td className="td-text-limited">
                    {parseDocumentType(model?.child_document_type)}
                  </td>
                  {shouldShowRouteColumn && (
                    <td className="td-text-limited">
                      {model?.child_document_route || SETTLEMENT_DOCUMENTS_MISSING_VALUE}
                    </td>
                  )}
                  <td className="td-text-limited">
                    {model?.charge_uid ? (
                      <Link
                        component="button"
                        onClick={() => window.open(`/cobros/${model.charge_uid}`, '_blank')}
                      >
                        {model.charge_uid}
                      </Link>
                    ) : (
                      SETTLEMENT_DOCUMENTS_MISSING_VALUE
                    )}
                  </td>
                  <td className="td-text-limited">
                    {isDocumentCredit(model)
                      ? SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CREDIT
                      : SETTLEMENT_DOCUMENTS_PAYMENT_TYPE_CASH}
                  </td>
                  <td className="td-text-limited td-number-align-right">
                    {formatMoney(Number(model?.child_document_balance))}
                  </td>
                  <td className="td-text-limited td-number-align-right">
                    {formatMoney(
                      Number(isDocumentCredit(model) ? 0 : model?.child_document_amount)
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={tableHeaders.length}>{SETTLEMENT_DOCUMENTS_EMPTY_STATE}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>
    </Box>
  )
}
